import { NormalNpc } from '../types';
import { GameConfig } from '../../core/GameConfig';
import { rankSystem } from '../../systems/RankSystem';
import { randomConfig } from '../../avatar/config';
import type { GiftCategory } from './Gifts';

/**
 * 普通 NPC 数据
 * 开局随机生成，不参与竞争，接戏后咖位成长
 */

/** 姓氏池 */
const SURNAMES = '李王张刘陈杨赵黄周吴徐孙马朱胡郭林何高罗'.split('');

/** 男性名字池（单字 + 双字混排） */
const MALE_NAMES = [
    '峥', '恪', '畅', '睿', '佑', '弈', '洋', '阳', '博', '谦', '淳', '凯', '宇', '驰', '骁', '铭',
    '尧', '杰', '曜', '昕', '煜', '晖', '锐', '琛', '易', '森', '渡', '琰', '天', '远',
    '嘉树', '志伟', '泽言', '思远', '景行', '沛霖', '博文', '一航', '昱珩', '彦霖', '明昊', '昊然',
    '彦嘉', '一博', '彦博', '皓轩', '奕辰', '沛然', '星宇', '明朗', '嘉乐', '一帆', '新杰', '英杰',
    '俊驰', '杰希', '宇宁', '家阳', '文韬', '展博', '铭宇',
];

/** 女性名字池（单字 + 双字混排） */
const FEMALE_NAMES = [
    '薇', '芸', '秋', '菲', '羽', '也', '潇', '沁', '甜', '月', '弥', '苏', '夏', '钰', '越', '慈',
    '灵', '妙', '盈', '瑛', '捷', '悦', '岚', '晴', '圆', '允', '溪', '辛', '澄', '淼',
    '晓彤', '冰冰', '琳梓', '嘉和', '诗然', '婧仪', '可欣', '琪琪', '祖儿', '清扬', '嘉怡', '遥真',
    '存希', '明羲', '宥仪', '汐儿', '宛萤', '明娜', '丽莎', '安心', '一诺', '安琪', '恩又', '翘楚',
    '怡然', '蓁蓁', '茗心', '云妮', '云开', '若蓁', '安宁',
];

/** 随机选取一个元素 */
function pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

/** 随机生成姓名 */
function randomName(gender: 'male' | 'female'): string {
    const surname = pick(SURNAMES);
    const given = gender === 'male' ? pick(MALE_NAMES) : pick(FEMALE_NAMES);
    return surname + given;
}

/**
 * 性格模板 key
 * 普通 NPC 全部是演员，无职业区分，按性格模板共享文案池
 */
export type PersonalityKey = 'cheerful' | 'quiet' | 'sharp' | 'warm';

/** 性格模板定义 */
export interface PersonalityTemplate {
    key: PersonalityKey;
    name: string;
    emoji: string;
    /** 聊天文案池（两段式「主角开场|NPC回复」） */
    chatTexts: string[];
    /** 邀约成功文案 */
    inviteOkText: string;
    /** 邀约失败文案 */
    inviteFailText: string;
    /** 赴约对话池（周末咖啡馆见面时 NPC 的见面对话，随机抽一条；与 inviteOkText 区分——后者是偶遇当场答应句） */
    inviteMeetTexts: string[];
    /** 送礼文案池（%s=喜好物名占位） */
    giftTexts: string[];
    /** 送礼喜好类别（自由出行批草案：cheerful→零食 / quiet→文创 / sharp→饰品 / warm→护肤；归文案填写表批改定） */
    favCategory: GiftCategory;
}

/** 四种性格模板及共享文案池 */
export const PERSONALITY_TEMPLATES: Record<PersonalityKey, PersonalityTemplate> = {
    cheerful: {
        key: 'cheerful',
        name: '开朗',
        favCategory: 'snack',
        emoji: '😊',
        chatTexts: [
            '最近天气真好，有没有什么好玩的事？|哈哈，我昨天去新开的游乐园了，超刺激！',
            '你最近拍戏累不累？|还好还好，有戏拍就开心，闲着才难受呢！',
            '听说你最近挺火的啊！|哪有哪有，就是运气好碰上了好剧本～',
            '一起吃饭去？|走啊走啊，我知道一家特别好吃的火锅店！',
            '你觉得我演技怎么样？|进步很大啊！我第一次看你演戏就觉得很自然。',
        ],
        inviteOkText: '太好了！我正好有空，咱们去哪玩？',
        inviteFailText: '哎呀真不巧，我今天有约了，下次一定！',
        inviteMeetTexts: [
            '嘿！你来得比我早啊，这家咖啡馆我之前就想来了！',
            '今天天气真好，适合出来逛逛，你选的地方真不错！',
            '哈哈我刚刚差点走错路，不过闻着咖啡味就找到了！',
        ],
        giftTexts: [
            '咱们这关系还送什么礼物呀！',
            '哇，是%s！你怎么知道我喜欢这个！',
        ],
    },
    quiet: {
        key: 'quiet',
        name: '内敛',
        favCategory: 'stationery',
        emoji: '🤫',
        chatTexts: [
            '最近在忙什么？|还是老样子，看看剧本，琢磨琢磨角色。',
            '你好像不太爱说话？|嗯…习惯了，戏里把话都说完了。',
            '这部戏拍得怎么样？|还行，导演挺严格的，但能学到东西。',
            '周末有什么安排？|打算在家看几部老电影，学习一下。',
            '觉得我穿这身怎么样？|不错，很适合你。',
        ],
        inviteOkText: '…好，那就去吧。',
        inviteFailText: '抱歉，今天不太方便。',
        inviteMeetTexts: [
            '…到了。你已经在等了。',
            '嗯，这家店挺安静的，不错。',
            '（坐下，看了看菜单）你常来这里？',
        ],
        giftTexts: [
            '谢谢，你有心了。',
            '…谢谢，我很喜欢。',
        ],
    },
    sharp: {
        key: 'sharp',
        name: '犀利',
        favCategory: 'acc',
        emoji: '😏',
        chatTexts: [
            '你觉得我演技怎么样？|说实话还是说好听的？',
            '最近试镜老是碰壁，好烦。|那不是正常的吗？谁不是被拒绝过来的。',
            '今天状态不太好…|那就别硬撑了，状态不好演出来也是垃圾。',
            '我这部戏造型怎么样？|还行吧，比上次那个强点。',
            '你说话真的好直接啊！|拐弯抹角的多累，浪费时间。',
        ],
        inviteOkText: '行啊，正好没事，陪你走一趟。',
        inviteFailText: '不去，今天没心情。',
        inviteMeetTexts: [
            '行，我来了。说吧，什么事非得约出来聊。',
            '这家店还行，比我想象的强。你眼光可以。',
            '别客气了，直接说吧，约我出来到底想聊什么。',
        ],
        giftTexts: [
            '嗨，这次我就收下了，下次别破费了啊。',
            '哟，%s？算你有眼光。',
        ],
    },
    warm: {
        key: 'warm',
        name: '温柔',
        favCategory: 'skincare',
        emoji: '🥰',
        chatTexts: [
            '今天拍戏辛苦了吧？|还好啦，倒是你看起来有点累，要好好休息哦。',
            '你觉得我适合什么角色？|我觉得你气质很好，文艺片或者生活剧都很适合你~',
            '最近压力好大。|别太勉强自己，你已经做得很好了。',
            '你真好，总是这么温柔。|嘿嘿，大家都不容易嘛，互相温暖一下。',
            '明天有什么安排？|明天有个通告，拍完打算去喝杯咖啡，要一起吗？',
        ],
        inviteOkText: '好呀好呀，和你一起我很开心~',
        inviteFailText: '啊，真抱歉，今天实在走不开，下次一定补上！',
        inviteMeetTexts: [
            '你来啦~我刚刚到，正想给你点一杯你喜欢的。',
            '这家咖啡馆好温馨，谢谢你约我出来，最近正好想放松一下~',
            '见到你真开心，今天咱们好好聊聊~',
        ],
        giftTexts: [
            '谢谢你送我的%s，太客气了~',
            '天哪，是%s！太感谢了，我超喜欢这个的！',
        ],
    },
};

/** 所有性格 key 列表 */
export const PERSONALITY_KEYS: PersonalityKey[] = ['cheerful', 'quiet', 'sharp', 'warm'];

/**
 * 生成一批普通 NPC
 * 按 NORMAL_NPC_RANK_DISTRIBUTION 分配初始咖位；性别走**定额洗牌**（`NORMAL_NPC_MALE_COUNT`）
 */
export function generateNormalNpcs(count: number): NormalNpc[] {
    const npcs: NormalNpc[] = [];
    let idCounter = 0;

    // 性别配额：旧写法是每人一次独立 50% 掷 ⇒ 15 人局能摇出 3 男 12 女（2026-10-04 实测 5 条周目里就有一条），
    // 影帝/男配两个类别无论排期里补多少本都凑不满 4 个**不同演员**（派角找不齐人就⛔ 出名单）⇒ 属结构死格。
    // 定成 8 男 7 女后仍然⛔ 与咖位绑定：先装满配额再整袋洗牌，逐位领性别（按顺序发会让高咖位整段同一性别）
    const genderBag: ('male' | 'female')[] = [];
    const maleCount = Math.min(count, GameConfig.NORMAL_NPC_MALE_COUNT);
    for (let i = 0; i < maleCount; i++) genderBag.push('male');
    for (let i = maleCount; i < count; i++) genderBag.push('female');
    for (let i = genderBag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [genderBag[i], genderBag[j]] = [genderBag[j], genderBag[i]];
    }

    // 按分布分配咖位
    const distribution = GameConfig.NORMAL_NPC_RANK_DISTRIBUTION;
    for (const [rank, cnt] of distribution) {
        for (let i = 0; i < cnt && npcs.length < count; i++) {
            const gender = genderBag[npcs.length];
            const personality = PERSONALITY_KEYS[Math.floor(Math.random() * PERSONALITY_KEYS.length)];
            const fame = rankSystem.getRankThreshold(rank);
            idCounter++;
            npcs.push({
                id: `npc_gen_${String(idCounter).padStart(4, '0')}`,
                name: randomName(gender),
                gender,
                personality,
                fame,
                rank,
                // 67 批：生成 NPC 的同时随机捏一张脸（config 仅 21 个小整数，随存档持久化，同一档内脸部稳定）
                avatar: randomConfig({ gender: gender === 'female' ? 'f' : 'm' }),
            });
        }
    }

    return npcs;
}